<template>
  <div class="w-full h-full p-4 overflow-auto">
    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Manual Distribution Lists -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold">Manuelle Verteiler</h2>
          <div class="flex gap-2">
            <button @click="showManualLists = !showManualLists" class="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">
              {{ showManualLists ? 'Ausblenden' : 'Einblenden' }}
            </button>
            <button v-if="showManualLists" @click="addNewList" class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              <font-awesome-icon icon="plus" class="mr-2" />
              Neuer Verteiler
            </button>
          </div>
        </div>

        <div v-if="showManualLists" class="space-y-4">
          <div v-for="(list, index) in lists" :key="list.id || `new-${index}`" 
            class="border rounded-lg p-4 hover:bg-gray-50">
            <div class="flex justify-end mb-3">
              <button
                v-if="list.id"
                @click="deleteList(list, index)"
                class="px-3 py-1 text-sm text-red-700 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                :disabled="deletingList === list.id"
              >
                <font-awesome-icon icon="trash" class="mr-1" />
                Löschen
              </button>
            </div>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input 
                  v-model="list.Name" 
                  type="text" 
                  placeholder="Verteiler-Name"
                  class="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail-Adressen (";" als Trennzeichen)
                </label>
                <textarea 
                  v-model="list.Liste" 
                  rows="3"
                  placeholder="email1@example.com;email2@example.com;email3@example.com"
                  class="w-full border rounded px-3 py-2"
                ></textarea>
              </div>
            </div>
            <div v-if="hasListChanges(list)" class="flex justify-end mt-3">
              <button 
                @click="saveList(list)"
                :disabled="!list.Name || !list.Liste || savingList === list"
                class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                <font-awesome-icon icon="save" class="mr-1" />
                {{ savingList === list ? 'Speichere...' : 'Speichern' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Recipient Selection -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold">Empfänger auswählen</h2>
          <button
            @click="resetSelection"
            class="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            Auswahl zurücksetzen
          </button>
        </div>

        <!-- All Registered Users -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-xl font-bold border-b-4 border-primary">Übergreifend</h3>
            <button
              @click="showAllRegistered = !showAllRegistered"
              class="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
            >
              {{ showAllRegistered ? 'Ausblenden' : 'Einblenden' }}
            </button>
          </div>
          <div v-if="showAllRegistered" class="flex items-center gap-2">
            <input 
              v-model="selection.allRegistered" 
              type="checkbox" 
              id="allRegistered" 
              class="small"
            />
            <label for="allRegistered">Alle registrierten Benutzer</label>
          </div>
        </div>

        <!-- Distribution Lists -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-xl font-bold border-b-4 border-primary">Verteiler</h3>
            <button
              @click="showDistributionLists = !showDistributionLists"
              class="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
            >
              {{ showDistributionLists ? 'Ausblenden' : 'Einblenden' }}
            </button>
          </div>
          <div v-if="showDistributionLists" class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div v-for="list in lists.filter(l => l.id)" :key="list.id" class="flex items-center gap-2">
              <input 
                v-model="selection.lists" 
                :value="list.id"
                type="checkbox" 
                :id="`list-${list.id}`" 
                class="small"
              />
              <label :for="`list-${list.id}`">{{ list.Name }}</label>
            </div>
          </div>
        </div>

        <!-- Active Projects -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-xl font-bold border-b-4 border-primary">Aktive Projekte</h3>
            <button
              @click="showActiveProjects = !showActiveProjects"
              class="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
            >
              {{ showActiveProjects ? 'Ausblenden' : 'Einblenden' }}
            </button>
          </div>
          <div v-if="showActiveProjects" class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div v-for="project in activeProjects" :key="project.id" class="flex items-center gap-2">
              <input 
                v-model="selection.projects" 
                :value="project.id"
                type="checkbox" 
                :id="`project-${project.id}`" 
                class="small"
              />
              <label :for="`project-${project.id}`">{{ project.Titel }}</label>
            </div>
          </div>
        </div>

        <!-- Archived Projects -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-xl font-bold border-b-4 border-primary">Archivierte Projekte</h3>
            <button
              @click="showArchivedProjects = !showArchivedProjects"
              class="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
            >
              {{ showArchivedProjects ? 'Ausblenden' : 'Einblenden' }}
            </button>
          </div>
          <div v-if="showArchivedProjects" class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div v-for="project in archivedProjects" :key="project.id" class="flex items-center gap-2">
              <input
                v-model="selection.projects"
                :value="project.id"
                type="checkbox"
                :id="`archived-${project.id}`"
                class="small"
              />
              <label :for="`archived-${project.id}`">{{ project.Titel }}</label>
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="border-t pt-6 space-y-4">
          <div class="flex items-center mb-3">
            <h3 class="text-lg font-semibold">Filter & Optionen</h3>
          </div>
          <div>
            <div class="flex items-center gap-2">
            <input 
              v-model="selection.onlyActive" 
              type="checkbox" 
              id="onlyActive" 
              class="small"
            />
            <label for="onlyActive">Nur aktuell laufende Verträge</label>
          </div>

          <div class="mt-4 space-y-2">
            <p class="text-sm font-medium text-gray-700">Vertragsstatus:</p>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div v-for="status in contractStatuses" :key="status.value" class="flex items-center gap-2">
                <input
                  v-model="selection.statuses"
                  :value="status.value"
                  type="checkbox"
                  :id="`contract-status-${status.value}`"
                  class="small"
                />
                <label :for="`contract-status-${status.value}`">{{ status.label }}</label>
              </div>
            </div>
          </div>

          <div class="space-y-2">
            <div class="flex flex-wrap gap-4">
              <div class="flex items-center gap-2">
                <input 
                  v-model="selection.parent1" 
                  type="checkbox" 
                  id="parent1" 
                  class="small"
                />
                <label for="parent1">Eltern 1</label>
              </div>
              <div class="flex items-center gap-2">
                <input 
                  v-model="selection.parent2" 
                  type="checkbox" 
                  id="parent2" 
                  class="small"
                />
                <label for="parent2">Eltern 2</label>
              </div>
              <div class="flex items-center gap-2">
                <input 
                  v-model="selection.subaccount" 
                  type="checkbox" 
                  id="subaccount" 
                  class="small"
                />
                <label for="subaccount">Unteraccount</label>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <input 
              v-model="selection.ignoreOptout" 
              type="checkbox" 
              id="optout" 
              class="small"
            />
            <label for="optout">
              Mail auch an Eltern senden, die KEINE Infomails erhalten wollen
            </label>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Trennzeichen</label>
            <select v-model="selection.separator" class="border rounded px-3 py-2">
              <option value=",">Komma (,)</option>
              <option value=";">Semikolon (;)</option>
            </select>
          </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="border-t pt-6 flex gap-3">
          <button 
            @click="prepareEmail" 
            :disabled="generating"
            class="px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            <font-awesome-icon icon="envelope" class="mr-2" />
            Mail vorbereiten
          </button>
          <button 
            @click="prepareNotification" 
            :disabled="generating"
            class="px-6 py-3 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            <font-awesome-icon icon="bell" class="mr-2" />
            Benachrichtigung vorbereiten
          </button>
        </div>

        <!-- Results -->
        <div v-if="recipients" class="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 class="font-semibold mb-2">Generierte Empfängerliste</h4>
          <p class="text-sm text-gray-600 mb-2">
            Anzahl: {{ recipients.count || recipients.addresses?.length || 0 }}
          </p>
          <div v-if="recipients.addresses" class="max-h-40 overflow-y-auto text-sm">
            <p class="font-mono">{{ recipients.addresses.join(selection.separator + ' ') }}</p>
          </div>
        </div>
      </div>

      <!-- Newsletter Section -->
      <div v-if="user && user.Newsletter > 0" class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold">Newsletter</h2>
          <button
            @click="showNewsletter = !showNewsletter"
            class="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
          >
            {{ showNewsletter ? 'Ausblenden' : 'Einblenden' }}
          </button>
        </div>

        <div v-if="showNewsletter" class="space-y-6">
          <!-- Newsletter Template Editor -->
          <div class="border rounded p-4 space-y-4">
            <h3 class="font-bold">Newsletter-Vorlage</h3>
            <div class="grid grid-cols-1 gap-3">
              <div><label class="block text-sm font-medium mb-1">Betreff</label><input v-model="newsletter.subject" class="w-full border rounded px-3 py-2"></div>
              <div><label class="block text-sm font-medium mb-1">Hero-Bild URL</label><input v-model="newsletter.hero" class="w-full border rounded px-3 py-2"></div>
              <div><label class="block text-sm font-medium mb-1">Überschrift 1</label><input v-model="newsletter.h1" class="w-full border rounded px-3 py-2"></div>
              <div><label class="block text-sm font-medium mb-1">Text 1</label><textarea v-model="newsletter.t1" rows="3" class="w-full border rounded px-3 py-2"></textarea></div>
              <div class="grid grid-cols-2 gap-3">
                <div><label class="block text-sm font-medium mb-1">Button 1 Text</label><input v-model="newsletter.b1_text" class="w-full border rounded px-3 py-2"></div>
                <div><label class="block text-sm font-medium mb-1">Button 1 URL</label><input v-model="newsletter.b1_url" class="w-full border rounded px-3 py-2"></div>
              </div>
              <div><label class="block text-sm font-medium mb-1">Überschrift 2</label><input v-model="newsletter.h2" class="w-full border rounded px-3 py-2"></div>
              <div>
                <label class="block text-sm font-medium mb-1">Listenelemente (eines pro Zeile)</label>
                <textarea v-model="newsletterL1Text" rows="4" class="w-full border rounded px-3 py-2" placeholder="Erstes Element&#10;Zweites Element"></textarea>
              </div>
              <div><label class="block text-sm font-medium mb-1">Text 2</label><textarea v-model="newsletter.t2" rows="3" class="w-full border rounded px-3 py-2"></textarea></div>
              <div class="grid grid-cols-2 gap-3">
                <div><label class="block text-sm font-medium mb-1">Button 2 Text</label><input v-model="newsletter.b2_text" class="w-full border rounded px-3 py-2"></div>
                <div><label class="block text-sm font-medium mb-1">Button 2 URL</label><input v-model="newsletter.b2_url" class="w-full border rounded px-3 py-2"></div>
              </div>
              <div><label class="block text-sm font-medium mb-1">Text 3 / Adresse</label><textarea v-model="newsletter.t3" rows="2" class="w-full border rounded px-3 py-2"></textarea></div>
              <div class="grid grid-cols-4 gap-3">
                <div><label class="block text-sm font-medium mb-1">Name</label><input v-model="newsletter.address_name" class="w-full border rounded px-3 py-2"></div>
                <div><label class="block text-sm font-medium mb-1">Straße</label><input v-model="newsletter.address_street" class="w-full border rounded px-3 py-2"></div>
                <div><label class="block text-sm font-medium mb-1">PLZ</label><input v-model="newsletter.address_zip" class="w-full border rounded px-3 py-2"></div>
                <div><label class="block text-sm font-medium mb-1">Stadt</label><input v-model="newsletter.address_city" class="w-full border rounded px-3 py-2"></div>
              </div>
            </div>
            <div class="flex gap-3">
              <button @click="saveNewsletter" :disabled="savingNewsletter" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                {{ savingNewsletter ? 'Speichere...' : 'Speichern' }}
              </button>
              <button @click="previewNewsletter" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Vorschau
              </button>
            </div>
            <div v-if="newsletterMsg" :class="newsletterSuccess ? 'text-green-600' : 'text-red-600'">{{ newsletterMsg }}</div>
          </div>

          <!-- Newsletter Send -->
          <div class="border rounded p-4 space-y-4" v-if="user.Newsletter >= 1">
            <h3 class="font-bold">Newsletter versenden</h3>
            <p class="text-sm text-gray-600">Verwendet die oben ausgewählten Projekte und Verteiler.</p>
            <div class="flex flex-wrap gap-3">
              <button @click="prepareNewsletterSend('Eltern1')" :disabled="sendingNewsletter" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                Eltern 1
              </button>
              <button @click="prepareNewsletterSend('Eltern2')" :disabled="sendingNewsletter" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                Eltern 2
              </button>
              <button @click="prepareNewsletterSend('Eltern1&2')" :disabled="sendingNewsletter" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                Eltern 1 & 2
              </button>
            </div>
            <div v-if="newsletterTargets" class="p-3 bg-gray-50 rounded">
              <p class="text-sm mb-2"><strong>{{ newsletterTargets.length }}</strong> Empfänger:</p>
              <div class="max-h-32 overflow-y-auto text-sm font-mono">{{ newsletterTargets.join(', ') }}</div>
              <button @click="confirmNewsletterSend" :disabled="sendingNewsletter" class="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                {{ sendingNewsletter ? 'Versende...' : 'Newsletter versenden' }}
              </button>
            </div>
            <div v-if="newsletterSendMsg" :class="newsletterSendSuccess ? 'text-green-600' : 'text-red-600'">{{ newsletterSendMsg }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Notification Modal -->
    <div v-if="showNotificationModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        <h2 class="text-2xl font-bold mb-4">Benachrichtigung erstellen</h2>
        
        <div class="mb-4 p-3 bg-blue-50 rounded">
          <p class="text-sm">
            Benachrichtigung würde an <strong>{{ notificationData.total_count }}</strong> Smartphones verschickt, 
            über die App registriert sind aber nur <strong>{{ notificationData.fcm_count }}</strong>.
          </p>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
            <input 
              v-model="notification.title" 
              type="text" 
              maxlength="100"
              class="w-full border rounded px-3 py-2"
              required
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Text *</label>
            <textarea 
              v-model="notification.text" 
              rows="3"
              maxlength="1000"
              class="w-full border rounded px-3 py-2"
              required
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Link (optional)</label>
            <input 
              v-model="notification.link" 
              type="url" 
              placeholder="https://..."
              class="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button 
            @click="closeNotificationModal" 
            class="px-4 py-2 border rounded hover:bg-gray-50"
            :disabled="sendingNotification"
          >
            Abbrechen
          </button>
          <button 
            @click="sendNotification" 
            :disabled="!notification.title || !notification.text || sendingNotification"
            class="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            <font-awesome-icon v-if="sendingNotification" icon="spinner" spin class="mr-2" />
            Benachrichtigungen versenden
          </button>
        </div>
      </div>
    </div>

    <!-- Email Modal -->
    <div v-if="showEmailModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        <h2 class="text-2xl font-bold mb-4">E-Mail vorbereiten</h2>
        
        <div class="mb-4 p-3 bg-blue-50 rounded">
          <p class="text-sm">
            <strong>{{ emailData.count }}</strong> Empfänger ausgewählt.
          </p>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Betreff (optional)</label>
            <input 
              v-model="emailSubject" 
              type="text" 
              placeholder="E-Mail-Betreff"
              class="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <div class="mt-6">
          <p class="text-sm font-medium text-gray-700 mb-3">E-Mail-Gruppen:</p>
          <div class="flex flex-wrap gap-3">
            <button 
              v-for="(chunk, index) in getEmailChunks()" 
              :key="index"
              @click="openMailto(chunk, index)"
              :class="clickedMailGroup === index ? 'px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700' : 'px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'"
            >
              <font-awesome-icon icon="envelope" class="mr-2" />
              Mailgruppe {{ index + 1 }} ({{ chunk.length }} Empfänger)
            </button>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button 
            @click="closeEmailModal" 
            class="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            @click="finishEmailModal"
            class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            <font-awesome-icon icon="check" class="mr-1" />
            Fertig
          </button>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6">
        <div class="text-lg font-semibold">Lade...</div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/API'

const CONTRACT_STATUS_OPTIONS = [
  { value: 'Eingegangen', label: 'Eingegangen' },
  { value: 'Vorgemerkt', label: 'Vorgemerkt' },
  { value: 'Bestätigt', label: 'Bestätigt' },
  { value: 'Gekündigt', label: 'Gekündigt' },
  { value: 'Kündigung eingegangen', label: 'Kündigung eingegangen' },
  { value: 'Warteliste', label: 'Warteliste' },
  { value: 'Absage', label: 'Abgesagt' },
  { value: 'Widerrufen', label: 'Widerrufen' },
  { value: 'Ausgelaufen', label: 'Ausgelaufen' }
]
const DEFAULT_CONTRACT_STATUSES = CONTRACT_STATUS_OPTIONS
  .filter(status => !['Absage', 'Widerrufen', 'Ausgelaufen'].includes(status.value))
  .map(status => status.value)

export default {
  name: 'DistributionLists',
  props: {
    user: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      loading: false,
      generating: false,
      lists: [],
      activeProjects: [],
      archivedProjects: [],
      showArchivedProjects: false,
      showManualLists: false,
      showAllRegistered: false,
      showDistributionLists: false,
      showActiveProjects: false,
      contractStatuses: CONTRACT_STATUS_OPTIONS,
      selection: {
        allRegistered: false,
        lists: [],
        projects: [],
        onlyActive: false,
        statuses: [...DEFAULT_CONTRACT_STATUSES],
        parent1: true,
        parent2: false,
        subaccount: false,
        ignoreOptout: false,
        separator: ';'
      },
      recipients: null,
      deletingList: null,
      savingList: null,
      showNotificationModal: false,
      notificationData: {
        total_count: 0,
        fcm_count: 0,
        fcm_users: []
      },
      notification: {
        title: '',
        text: '',
        link: ''
      },
      sendingNotification: false,
      showEmailModal: false,
      emailData: {
        addresses: [],
        count: 0
      },
      emailSubject: '',
      clickedMailGroup: null,
      // Newsletter
      showNewsletter: false,
      newsletter: {
        subject: '', hero: '', h1: '', t1: '', b1_text: '', b1_url: '',
        h2: '', l1: [], t2: '', b2_text: '', b2_url: '', t3: '',
        address_name: '', address_street: '', address_zip: '', address_city: ''
      },
      newsletterL1Text: '',
      savingNewsletter: false,
      newsletterMsg: null,
      newsletterSuccess: false,
      newsletterTargets: null,
      sendingNewsletter: false,
      newsletterSendMsg: null,
      newsletterSendSuccess: false,
    }
  },
  async created() {
    await this.loadData()
    if (this.user && this.user.Newsletter > 0) {
      this.loadNewsletter()
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadLists(),
          this.loadProjects()
        ])
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        this.loading = false
      }
    },

    async loadLists() {
      return new Promise((resolve, reject) => {
        API.getDistributionLists((data, status) => {
          if (status === 200) {
            this.lists = (data.lists || []).map(list => ({
              ...list,
              savedName: list.Name,
              savedListe: list.Liste
            }))
            resolve()
          } else {
            reject(new Error('Failed to load lists'))
          }
        })
      })
    },

    async loadProjects() {
      return new Promise((resolve, reject) => {
        API.getProjectsForLists((data, status) => {
          if (status === 200) {
            this.activeProjects = data.active || []
            this.archivedProjects = data.archived || []
            resolve()
          } else {
            reject(new Error('Failed to load projects'))
          }
        })
      })
    },

    addNewList() {
      this.lists.push({
        Name: '',
        Liste: '',
        savedName: '',
        savedListe: '',
        isNew: true
      })
    },

    hasListChanges(list) {
      return list.Name !== list.savedName || list.Liste !== list.savedListe
    },

    async saveList(list) {
      if (!list.Name || !list.Liste || this.savingList) return

      this.savingList = list
      try {
        if (list.isNew || !list.id) {
          // Create new list
          const response = await new Promise((resolve, reject) => {
            API.createDistributionList({
              Name: list.Name,
              Liste: list.Liste
            }, (data, status) => {
              if (status === 201) {
                resolve(data)
              } else {
                reject(new Error('Failed to create list'))
              }
            })
          })
          
          Object.assign(list, response)
          delete list.isNew
        } else {
          // Update existing list
          await new Promise((resolve, reject) => {
            API.updateDistributionList(list.id, {
              Name: list.Name,
              Liste: list.Liste
            }, (data, status) => {
              if (status === 200) {
                resolve(data)
              } else {
                reject(new Error('Failed to update list'))
              }
            })
          })
        }

        list.savedName = list.Name
        list.savedListe = list.Liste
        this.$toast?.open?.({
          message: 'Verteiler gespeichert',
          type: 'success',
          position: 'top'
        })
      } catch (error) {
        console.error('Error saving list:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Speichern',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.savingList = null
      }
    },

    async deleteList(list, index) {
      if (!confirm('Soll dieser Verteiler wirklich gelöscht werden?')) return

      if (!list.id) {
        this.lists.splice(index, 1)
        return
      }

      this.deletingList = list.id
      try {
        await new Promise((resolve, reject) => {
          API.deleteDistributionList(list.id, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to delete list'))
            }
          })
        })

        this.lists.splice(index, 1)
        
        this.$toast?.open?.({
          message: 'Verteiler gelöscht',
          type: 'success',
          position: 'top'
        })
      } catch (error) {
        console.error('Error deleting list:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Löschen',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.deletingList = null
      }
    },

    async prepareEmail() {
      this.generating = true
      try {
        const response = await new Promise((resolve, reject) => {
          API.generateRecipients({
            allRegistered: this.selection.allRegistered,
            projects: this.selection.projects,
            lists: this.selection.lists,
            onlyActive: this.selection.onlyActive,
            statuses: this.selection.statuses,
            parent1: this.selection.parent1,
            parent2: this.selection.parent2,
            subaccount: this.selection.subaccount,
            ignoreOptout: this.selection.ignoreOptout,
            type: 'email'
          }, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to generate recipients'))
            }
          })
        })

        this.recipients = response

        if (response.addresses && response.addresses.length > 0) {
          // Show email modal
          this.emailData = {
            addresses: response.addresses,
            count: response.addresses.length
          }
          this.showEmailModal = true
        } else {
          this.$toast?.open?.({
            message: 'Keine Empfänger gefunden',
            type: 'warning',
            position: 'top'
          })
        }
      } catch (error) {
        console.error('Error preparing email:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Vorbereiten der E-Mail',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.generating = false
      }
    },

    async prepareNotification() {
      this.generating = true
      try {
        const response = await new Promise((resolve, reject) => {
          API.generateRecipients({
            allRegistered: this.selection.allRegistered,
            projects: this.selection.projects,
            lists: this.selection.lists,
            onlyActive: this.selection.onlyActive,
            statuses: this.selection.statuses,
            parent1: this.selection.parent1,
            parent2: this.selection.parent2,
            subaccount: this.selection.subaccount,
            ignoreOptout: this.selection.ignoreOptout,
            type: 'notification'
          }, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to generate recipients'))
            }
          })
        })

        this.recipients = response

        if (response.fcm_users && response.fcm_users.length > 0) {
          // Open notification modal
          this.notificationData = {
            total_count: response.total_count,
            fcm_count: response.fcm_count,
            fcm_users: response.fcm_users
          }
          this.showNotificationModal = true
        } else {
          this.$toast?.open?.({
            message: 'Keine Empfänger mit Push-Benachrichtigungen gefunden',
            type: 'warning',
            position: 'top'
          })
        }
      } catch (error) {
        console.error('Error preparing notification:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Vorbereiten der Benachrichtigung',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.generating = false
      }
    },

    closeNotificationModal() {
      this.showNotificationModal = false
      this.notification = {
        title: '',
        text: '',
        link: ''
      }
    },

    closeEmailModal() {
      this.showEmailModal = false
      this.emailSubject = ''
      this.clickedMailGroup = null
    },

    finishEmailModal() {
      this.closeEmailModal()
      this.resetSelection()
      this.selection.parent1 = false
    },

    getEmailChunks() {
      const chunkSize = 495
      const chunks = []
      for (let i = 0; i < this.emailData.addresses.length; i += chunkSize) {
        chunks.push(this.emailData.addresses.slice(i, i + chunkSize))
      }
      return chunks
    },

    openMailto(chunk, index) {
      const contactMail = process.env.VUE_APP_CONTACT_MAIL || 'info@example.com'
      const bcc = chunk.join(this.selection.separator)
      let mailtoLink = `mailto:${contactMail}?bcc=${encodeURIComponent(bcc)}`
      if (this.emailSubject) {
        mailtoLink += `&subject=${encodeURIComponent(this.emailSubject)}`
      }
      this.clickedMailGroup = index
      window.open(mailtoLink, '_blank')
    },

    async sendNotification() {
      if (!this.notification.title || !this.notification.text) return

      this.sendingNotification = true
      try {
        await new Promise((resolve, reject) => {
          API.sendAppNotification({
            recipients: this.notificationData.fcm_users.join(';'),
            title: this.notification.title,
            text: this.notification.text,
            link: this.notification.link
          }, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to send notification'))
            }
          })
        })

        this.$toast?.open?.({
          message: `Benachrichtigung an ${this.notificationData.fcm_count} Benutzer gesendet`,
          type: 'success',
          position: 'top'
        })

        this.closeNotificationModal()
      } catch (error) {
        console.error('Error sending notification:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Senden der Benachrichtigung',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.sendingNotification = false
      }
    },

    resetSelection() {
      this.selection = {
        allRegistered: false,
        lists: [],
        projects: [],
        onlyActive: false,
        statuses: [...DEFAULT_CONTRACT_STATUSES],
        parent1: true,
        parent2: false,
        subaccount: false,
        ignoreOptout: false,
        separator: ';'
      }
      this.recipients = null
    },

    // Newsletter methods
    loadNewsletter() {
      API.getNewsletter((r) => {
        if (r && r.id) {
          this.newsletter = r
          this.newsletterL1Text = (r.l1 || []).join('\n')
        }
      })
    },

    getNewsletterData() {
      const data = { ...this.newsletter }
      data.l1 = this.newsletterL1Text.split('\n').filter(l => l.trim() !== '')
      return data
    },

    saveNewsletter() {
      this.savingNewsletter = true
      this.newsletterMsg = null
      API.updateNewsletter(this.getNewsletterData(), (r, status) => {
        this.savingNewsletter = false
        if (status === 200) {
          this.newsletterSuccess = true
          this.newsletterMsg = 'Newsletter gespeichert.'
        } else {
          this.newsletterSuccess = false
          this.newsletterMsg = 'Speichern fehlgeschlagen.'
        }
      })
    },

    previewNewsletter() {
      const data = this.getNewsletterData()
      API.previewNewsletter(data, (html, status) => {
        if (status === 200 && typeof html === 'string') {
          const w = window.open('', '_blank')
          w.document.write(html)
          w.document.close()
        } else {
          this.newsletterSuccess = false
          this.newsletterMsg = 'Vorschau fehlgeschlagen.'
        }
      })
    },

    prepareNewsletterSend(parentType) {
      this.sendingNewsletter = true
      this.newsletterSendMsg = null
      this.newsletterTargets = null
      API.prepareNewsletterSend({
        projects: this.selection.projects,
        lists: this.selection.lists,
        parentType: parentType,
        ignoreOptout: this.selection.ignoreOptout,
      }, (r, status) => {
        this.sendingNewsletter = false
        if (status === 200) {
          this.newsletterTargets = r.targets
        } else {
          this.newsletterSendSuccess = false
          this.newsletterSendMsg = 'Empfänger konnten nicht ermittelt werden.'
        }
      })
    },

    confirmNewsletterSend() {
      if (!this.newsletterTargets || this.newsletterTargets.length === 0) return
      this.sendingNewsletter = true
      this.newsletterSendMsg = null
      API.confirmNewsletterSend({ targets: this.newsletterTargets }, (r, status) => {
        this.sendingNewsletter = false
        if (status === 200) {
          this.newsletterSendSuccess = true
          this.newsletterSendMsg = r.message
          this.newsletterTargets = null
        } else {
          this.newsletterSendSuccess = false
          this.newsletterSendMsg = r.message || 'Versand fehlgeschlagen.'
        }
      })
    }
  }
}
</script>

<style scoped>
</style>
